import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Icons } from '@/utils/Icons';
import { useSidebar } from '@/hooks/useSidebar';
import { useTheme } from '@/context/ThemeContext';

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const [sportsOpen, setSportsOpen] = useState(false);
  const [casinoOpen, setCasinoOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const {
    sportsData,
    openSport,
    openCompetition,
    selectedSport,
    competitions,
    competitionsLoading,
    events,
    eventsLoading,
    toggleSport,
    toggleCompetition,
  } = useSidebar();

  const sidebarItems = [
    { id: 1, title: 'Home', path: '/', imgUrl: '/images/sidebar/home-icon.png' },
    { id: 2, title: 'In-Play', path: '/inplay', imgUrl: '/images/sidebar/inplay-icon.png' },
    {
      id: 3,
      title: 'WWC 2026',
      path: '/sports/cricket/market-details/9922',
      imgUrl: '/images/sidebar/winner-icon.png',
    },
  ];

  const otherItems = [
    { id: 1, title: 'Supports', path: '/support', imgUrl: '/images/sidebar/support-icon.png' },
    { id: 2, title: 'FAQ', path: '/faq', imgUrl: '/images/sidebar/faq-icon.png' },
  ];

  const casinoItems = [
    { id: 8, title: 'indian poker', imgUrl: '/images/header/indianpoker-icon.png' },
    { id: 9, title: 'aviator', imgUrl: '/images/header/aviator-icon.png' },
    { id: 10, title: 'chicken road', imgUrl: '/images/header/chickenroll-icon.png' },
    { id: 11, title: 'live casino', imgUrl: '/images/header/livecasino-icon.png' },
    { id: 12, title: 'int.casino', imgUrl: '/images/header/intcasino-icon.png' },
    { id: 13, title: 'slot', imgUrl: '/images/header/slot-icon.png' },
  ];

  const handleSupportClick = () => {
    const phone = '919091920400';
    const message = encodeURIComponent('Hi');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    setSidebarOpen(false);
  };

  return (
    <>
      {/* ── Mobile overlay backdrop ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[998] bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar panel ── */}
      <div
        className={`
          fixed top-[70px] left-0 bottom-0 z-[999] overflow-y-auto bg-white dark:bg-darkPanelBg p-[10px] pt-9
          shadow-[1.95px_1.95px_2.6px_#00000026] transition-transform duration-300
          w-[252px]
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        <ul className="w-full">
          {sidebarItems.map((item) => (
            <li key={item.id} className="mb-[2px]">
              <NavLink
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] flex items-center w-full py-1 px-[10px] text-sm font-bold rounded-full ${
                    isActive ? 'bg-[linear-gradient(90deg,#fec733,#18162a00)]' : ''
                  }`
                }
              >
                <span className="flex h-8 w-8 mr-[5px] bg-[#1f2c38] rounded-full justify-center items-center">
                  <img src={item.imgUrl} alt={item.title} className="w-8 h-auto" />
                </span>
                {item.title}
              </NavLink>
            </li>
          ))}

          {/* SPORTS */}
          <li>
            <div
              onClick={() => setSportsOpen(!sportsOpen)}
              className="flex items-center w-full py-2 px-[10px] text-sm font-bold relative cursor-pointer"
            >
              <span className="flex h-8 w-8 mr-[5px] bg-[#1f2c38] rounded-full justify-center items-center">
                <img src="/images/sidebar/sports-icon.png" alt="sports" className="w-8 h-auto" />
              </span>
              <span>Sports</span>
              <Icons.CaretDown
                className={`absolute right-[10px] transition-transform duration-300 ${
                  sportsOpen ? 'rotate-0' : '-rotate-90'
                }`}
              />
            </div>

            {sportsOpen && (
              <div className="text-xs px-[5px] py-[2px] rounded-[5px] font-medium">
                <div className="w-full">
                  {sportsData.map((sport) => (
                    <div key={sport.id} className="pl-5 my-1">
                      {/* Sport row */}
                      <div
                        onClick={() => toggleSport(sport.id)}
                        className="flex items-center gap-[5px] relative h-[35px] hover:bg-[linear-gradient(90deg,#05960b,#18162a00)] rounded-l-[50px] cursor-pointer"
                      >
                        <img className="w-8 h-auto" src={sport.imgUrl} alt="" />
                        <span className="w-[140px] max-w-[140px]">{sport.name}</span>
                        {sport.apiName && (
                          <span className="absolute right-0">
                            {openSport === sport.id ? (
                              <Icons.MinusSquare size={12} />
                            ) : (
                              <Icons.PlusSquare size={12} />
                            )}
                          </span>
                        )}
                      </div>

                      {/* Competitions list */}
                      {openSport === sport.id && sport.apiName && (
                        <div className="border-l-2 border-orange-400">
                          {competitionsLoading ? (
                            <p className="text-xs py-2 pl-4 text-gray-500">Loading...</p>
                          ) : competitions.length === 0 ? (
                            <p className="text-xs py-2 pl-4 text-gray-500">No tournaments</p>
                          ) : (
                            competitions.map((competition) => (
                              <div key={competition.id}>
                                {/* Competition row */}
                                <div
                                  onClick={() => toggleCompetition(competition.id)}
                                  className="flex items-center justify-between w-full py-2 pl-4 pr-2 text-left relative cursor-pointer hover:text-green-700"
                                >
                                  <span>{competition.name}</span>
                                  <span className="absolute right-0">
                                    {openCompetition === competition.id ? (
                                      <Icons.MinusSquare size={12} />
                                    ) : (
                                      <Icons.PlusSquare size={12} />
                                    )}
                                  </span>
                                </div>

                                {/* Matches list */}
                                {openCompetition === competition.id && sport.urlPath && (
                                  <div className="ml-4 border-l-2 border-orange-400">
                                    {eventsLoading ? (
                                      <p className="text-xs py-2 pl-4 text-gray-500">Loading...</p>
                                    ) : events.length === 0 ? (
                                      <p className="text-xs py-2 pl-4 text-gray-500">No matches</p>
                                    ) : (
                                      events.map((event) => (
                                        <NavLink
                                          key={event.id}
                                          to={`/sports/${sport.urlPath}/market-details/${event.id}`}
                                          onClick={() => setSidebarOpen(false)}
                                          className={({ isActive }) =>
                                            `block py-2 pl-4 pr-2 text-xs cursor-pointer hover:text-green-700 ${
                                              isActive ? 'font-semibold text-green-700' : ''
                                            }`
                                          }
                                        >
                                          {event.name}
                                        </NavLink>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </li>

          {/* CASINO */}
          <li className="mt-2">
            <button
              onClick={() => setCasinoOpen(!casinoOpen)}
              className="flex items-center w-full py-2 px-[10px] text-sm font-bold"
            >
              <span className="flex h-8 w-8 mr-[5px] bg-[#1f2c38] rounded-full justify-center items-center">
                <img src="/images/sidebar/casino-icon.png" alt="casino" className="w-8 h-auto" />
              </span>
              <span>Casino</span>
              <Icons.CaretDown
                className={`ml-auto transition-transform duration-300 ${
                  casinoOpen ? 'rotate-0' : '-rotate-90'
                }`}
              />
            </button>

            {casinoOpen && (
              <div className="ml-10 mt-2 space-y-3 text-xs">
                {casinoItems.map((casino) => (
                  <div
                    key={casino.id}
                    className="capitalize h-[35px] flex items-center gap-[5px] hover:bg-[linear-gradient(90deg,#05960b,#18162a00)] rounded-l-[50px] font-medium"
                  >
                    <img className="w-8 h-auto" src={casino.imgUrl} alt="" />
                    <span className="w-[140px] max-w-[140px]">{casino.title}</span>
                  </div>
                ))}
              </div>
            )}
          </li>

          {otherItems.map((item) => (
            <li key={item.id} className="mb-[2px]">
              {item.title === 'Supports' ? (
                <button
                  type="button"
                  onClick={handleSupportClick}
                  className="hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] flex items-center w-full py-2 px-[10px] text-sm font-bold rounded-full text-left"
                >
                  <span className="flex h-8 w-8 mr-[5px] bg-[#1f2c38] rounded-full justify-center items-center">
                    <img src={item.imgUrl} alt={item.title} className="w-8 h-auto" />
                  </span>
                  {item.title}
                </button>
              ) : (
                <NavLink
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `hover:bg-[linear-gradient(90deg,#fec733,#18162a00)] flex items-center w-full py-2 px-[10px] text-sm font-bold rounded-full ${
                      isActive ? 'bg-[linear-gradient(90deg,#fec733,#18162a00)]' : ''
                    }`
                  }
                >
                  <span className="flex h-8 w-8 mr-[5px] bg-[#1f2c38] rounded-full justify-center items-center">
                    <img src={item.imgUrl} alt={item.title} className="w-8 h-auto" />
                  </span>
                  {item.title}
                </NavLink>
              )}
            </li>
          ))}

          <li className="mt-2 mb-2">
            <button
              className="w-full py-2 rounded-full bg-[#111020] text-white font-bold border border-secondaryBg hover:bg-[#FEC733] transition-colors"
              style={{ filter: 'drop-shadow(1px 2px 4px #ffd04e)' }}
            >
              Download APK
            </button>
          </li>

          <li className="mt-2 mb-2">
            <div className="flex items-baseline justify-between w-full">
              <div className="flex items-center w-3/4 text-sm font-bold">
                <span className="flex h-8 mr-[5px] overflow-hidden align-bottom w-8 bg-[#1f2c38] justify-center items-center rounded-[50px]">
                  <img
                    src={
                      theme === 'dark'
                        ? '/images/sidebar/lighttheme-icon.png'
                        : '/images/sidebar/darktheme-icon.png'
                    }
                    className="w-8 h-auto"
                    alt=""
                  />
                </span>
                <span>{theme === 'dark' ? 'Light Theme' : 'Dark Theme'}</span>
              </div>
              <button
                onClick={toggleTheme}
                className={`relative w-11 h-6 rounded-full transition-all duration-300 ${
                  theme === 'dark' ? 'bg-[#FEC733]' : 'bg-[#3b3b3b]'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-300 ${
                    theme === 'dark' ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
          </li>
        </ul>
      </div>
    </>
  );
};

export default Sidebar;
